import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  Briefcase, Users, AlertCircle, CalendarClock, FileText,
  Video, MessageSquare,
} from "lucide-react";
import { getDashboardRecruitment } from "../api";
import { useAuth } from "../context/AuthContext";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { formatDate, formatDateTime, tenantHour } from "../utils/dateFormat";
import { StatCard, QuickAction } from "../components/DashboardWidgets";
import ModuleSetupCard from "../components/ModuleSetupCard";

const NAVY = "#1e3a8a";
const TEAL = "#0f766e";
const AMBER = "#f59e0b";
const RED = "#dc2626";
const PURPLE = "#7c3aed";

const STAGE_LABELS: Record<string, string> = {
  new: "New",
  reviewed: "Reviewed",
  shortlisted: "Shortlisted",
  hired: "Hired",
};

const INTERVIEW_TYPE_LABELS: Record<string, string> = {
  video: "Video",
  in_person: "In person",
  phone: "Phone",
};

function activityDot(type: string, status: string) {
  if (type === "offer") {
    return status === "accepted" ? "#16a34a" : status === "declined" ? RED : AMBER;
  }
  if (type === "interview") return NAVY;
  return PURPLE;
}

function activityIcon(type: string) {
  if (type === "offer") return <FileText size={14} />;
  if (type === "interview") return <Video size={14} />;
  return <Users size={14} />;
}

const card: React.CSSProperties = {
  background: "white", borderRadius: 10, padding: "22px 24px",
  border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

// Recruitment module's dashboard — module home for the Recruitment section
// (spec: .claude/specs/recruitment-dashboard.md). Follows FinanceDashboardPage's
// extraction pattern: a dedicated page replacing the generic ModuleDashboardPage
// entry, fed by one aggregate endpoint (/api/dashboard/recruitment).
export default function RecruitmentDashboardPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["dashboard-recruitment"],
    queryFn: getDashboardRecruitment,
    refetchInterval: 60_000,
  });

  const firstName = (user?.name || "").split(" ")[0];
  const hour = tenantHour(tz);
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dayStr = formatDate(new Date(), "long", tz);

  const stats = data?.stats;
  const funnel = (data?.funnel ?? []).map((f: any) => ({ ...f, label: STAGE_LABELS[f.stage] || f.stage }));
  const hasFunnelData = funnel.some((f: any) => f.count > 0);
  const upcoming: any[] = data?.upcoming_interviews ?? [];
  const jobHealth: any[] = data?.job_health ?? [];
  const activity: any[] = data?.activity ?? [];
  const timeToHire: any[] = data?.time_to_hire ?? [];
  const hasHires = timeToHire.some((m: any) => m.hires > 0);

  return (
    <div>
      {/* No width cap: the module's own list pages (Candidates, Jobs, Offers,
          Interview Hub) all run the full content width, so capping the module
          landing page made the layout jump as you moved between them. */}

      {/* ── Greeting ── */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: -0.5 }}>
          {greeting}, {firstName} 👋
        </h1>
        <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>{dayStr} · Here's your hiring pipeline</p>
      </div>

      <ModuleSetupCard module="recruitment" />

      {/* ── Stat cards ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 28 }} className="stat-cards-row">
        <StatCard label="Open Jobs" value={stats?.open_jobs ?? 0} color={NAVY}
          icon={<Briefcase size={18} />} to="/jobs" subtitle="Actively hiring" />
        <StatCard label="New Candidates" value={stats?.new_candidates ?? 0} color={PURPLE}
          icon={<Users size={18} />} to="/candidates" subtitle="Awaiting review" />
        <StatCard label="Unreviewed 5+ Days" value={stats?.stale_candidates ?? 0}
          color={(stats?.stale_candidates ?? 0) > 0 ? AMBER : TEAL}
          icon={<AlertCircle size={18} />} to="/candidates"
          subtitle={(stats?.stale_candidates ?? 0) > 0 ? "Needs attention" : "All caught up"} />
        <StatCard label="Interviews This Week" value={stats?.interviews_this_week ?? 0} color={TEAL}
          icon={<CalendarClock size={18} />} to="/interview-hub" subtitle="Next 7 days" />
        <StatCard label="Offers Awaiting" value={stats?.offers_awaiting ?? 0} color={RED}
          icon={<FileText size={18} />} to="/offers" subtitle="Sent, no response" />
      </div>

      {/* ── Quick actions ── */}
      <div style={{ ...card, padding: "20px 24px", marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 14 }}>Quick Actions</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <QuickAction label="Post a Job" icon={<Briefcase size={14} />} to="/jobs/new" color={NAVY} />
          <QuickAction label="View Candidates" icon={<Users size={14} />} to="/candidates" color={PURPLE} />
          <QuickAction label="Talent Pool" icon={<Users size={14} />} to="/talent-pool" color={TEAL} />
          <QuickAction label="Offer Letters" icon={<FileText size={14} />} to="/offers" color={AMBER} />
          <QuickAction label="Interview Hub" icon={<Video size={14} />} to="/interview-hub" color={NAVY} />
          <QuickAction label="AI Screening" icon={<MessageSquare size={14} />} to="/screening" color={RED} />
        </div>
      </div>

      {/* ── Funnel + upcoming interviews ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }} className="grid-2col">

        <div style={card}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Pipeline Funnel</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Candidates in open jobs · by stage</div>
          </div>
          {!hasFunnelData ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b", fontSize: 13 }}>
              No candidates in the pipeline yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={funnel} barSize={34}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} width={24} />
                <Tooltip cursor={{ fill: "#f8fafc" }} formatter={(v: number) => [v, "Candidates"]} />
                <Bar dataKey="count" name="Candidates" fill={PURPLE} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Upcoming Interviews</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Next 7 days</div>
            </div>
            {(data?.pending_feedback ?? 0) > 0 && (
              <button onClick={() => navigate("/interview-hub")} style={{
                border: "none", cursor: "pointer", background: "#fef2f2", color: RED,
                fontSize: 12, fontWeight: 700, padding: "5px 10px", borderRadius: 999,
              }}>
                {data?.pending_feedback} awaiting feedback
              </button>
            )}
          </div>
          {upcoming.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 13 }}>
              No interviews scheduled this week
            </div>
          ) : (
            <div>
              {upcoming.map((iv, i) => (
                <div key={iv.id} role="button" tabIndex={0}
                  onClick={() => navigate("/interview-hub")}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/interview-hub"); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                    padding: "9px 0", borderBottom: i < upcoming.length - 1 ? "1px solid #f1f5f9" : "none",
                  }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                    background: `${TEAL}15`, color: TEAL,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Video size={14} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {iv.candidate_name}{iv.job_title ? ` · ${iv.job_title}` : ""}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                      {iv.scheduled_at ? formatDateTime(new Date(iv.scheduled_at), tz) : ""}
                      {iv.interviewer_name ? ` · ${iv.interviewer_name}` : ""}
                      {" · "}{INTERVIEW_TYPE_LABELS[iv.interview_type] || iv.interview_type}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Job health + activity ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }} className="grid-2col">

        <div style={card}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Job Health</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Open jobs · most unreviewed first</div>
          </div>
          {jobHealth.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 13 }}>No open jobs</div>
          ) : (
            <div>
              {jobHealth.map((j, i) => (
                <div key={j.job_id} role="button" tabIndex={0}
                  onClick={() => navigate(`/jobs/${j.job_id}`)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/jobs/${j.job_id}`); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                    padding: "10px 0", borderBottom: i < jobHealth.length - 1 ? "1px solid #f1f5f9" : "none",
                  }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {j.title}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                      {j.total} candidate{j.total !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: PURPLE, background: `${PURPLE}12`, padding: "3px 8px", borderRadius: 999 }}>
                      {j.new} new
                    </span>
                    {j.stale > 0 && (
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: AMBER, background: `${AMBER}18`, padding: "3px 8px", borderRadius: 999 }}>
                        {j.stale} stale
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={card}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>Recent Activity</div>
          {activity.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 13 }}>No recent activity</div>
          ) : (
            <div>
              {activity.slice(0, 7).map((item, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "9px 0", borderBottom: i < Math.min(activity.length, 7) - 1 ? "1px solid #f1f5f9" : "none",
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0, marginTop: 1,
                    background: `${activityDot(item.type, item.status)}15`,
                    color: activityDot(item.type, item.status),
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {activityIcon(item.type)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.text}
                    </div>
                    {item.sub && <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>{item.sub}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Time-to-hire + offers & screening ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} className="grid-2col">

        <div style={card}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Time to Hire</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Median days from application to hire · last 6 months</div>
          </div>
          {!hasHires ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b", fontSize: 13 }}>No hires yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={timeToHire} barSize={18} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} width={24} />
                <Tooltip cursor={{ fill: "#f8fafc" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="median_days" name="Median Days" fill={TEAL} radius={[4, 4, 0, 0]} />
                <Bar dataKey="hires" name="Hires" fill={PURPLE} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div style={card}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>Offers & Screening</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(120px, 1fr))", gap: 12 }}>
            {[
              { label: "Offers awaiting response", value: data?.offers?.awaiting ?? 0, color: AMBER, to: "/offers" },
              { label: "Accepted this month", value: data?.offers?.accepted_this_month ?? 0, color: "#16a34a", to: "/offers" },
              { label: "Declined this month", value: data?.offers?.declined_this_month ?? 0, color: RED, to: "/offers" },
              { label: "Screenings to review", value: data?.screening?.awaiting_review ?? 0, color: NAVY, to: "/screening" },
            ].map(({ label, value, color, to }) => (
              <div key={label} role="button" tabIndex={0}
                onClick={() => navigate(to)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(to); }}
                style={{
                  background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
                  padding: "12px 14px", cursor: "pointer",
                }}>
                <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
